import React, { FC, useCallback, useContext, useMemo } from 'react';
import { ProductPlansFetcher } from '../../fetchers/ProductPlansFetcher';
import { IServerSorting, useAutoFetch } from '../../contexts/Fetcher';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowParams } from '@mui/x-data-grid';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { DateFormat } from '../../helpers/dateFormat';
import { IAdminPlan } from '../../models/AdminPlan';
import { AddProductPlanDialog } from './AddProductPlanDialog';
import { useLocation } from 'react-router-dom';
import { EditProductPlanDialog } from './EditProductPlanDialog';
import { ProductPlanFetcher } from '../../fetchers/ProductPlanFetcher';
import { CellContentTooltip } from '../CellContentTooltip/CellContentTooltip';
import { useTranslation } from 'react-i18next';
import { BoolChip } from '../BoolChip/BoolChip';
import { CollapsedContentsWithTooltip } from '../CollapsedContentsWithTooltip/CollapsedContentsWithTooltip';
import { getBoolFilterOperator } from '../../filterOperators/boolFilterOperator';
import { IQuickFiltersProps } from '../EasyFilter/QuickFilters';
import { EasyFilter } from '../EasyFilter/EasyFilter';
import { useDialog } from '../../hooks/useDialog';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { apiProductPlan } from '../../resources/api/apiProductPlan';
import { useResourceChecker } from '../../resources/resource';
import { useIntQueryParam } from '../../hooks/useQueryParam';
import { queryUrl } from '../../hooks/useQueryUrl';
import { FilteringContextProvider } from '../../contexts/FilteringContext';

const { useUpdatePlan } = apiProductPlan;

const defaultSort: IServerSorting = {
  sortBy: 'id',
  sortOrder: 'asc',
};

export const ProductPlans: FC = () => {
  const { data } = useContext(ProductPlansFetcher.Context);
  useAutoFetch(ProductPlansFetcher);

  const { isOpen: isAdding, close: closeDialog } = useDialog('new');

  const abortEdit = useCloseDialog(void 0, ['edit']);

  const id = useIntQueryParam('edit');
  const { resource: resourceUpdate } = useUpdatePlan(0);
  const resourceChecker = useResourceChecker();

  const { search } = useLocation();

  const { t } = useTranslation();

  const quickFilters = useMemo<IQuickFiltersProps['quickFilters']>(
    () =>
      new Map([
        [
          t('common.quickFilter.valid'),
          {
            filterField: 'archived',
            filterOp: 'equals',
            filterVal: 'false',
            isDefault: true,
          },
        ],
      ]),
    []
  );

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'id',
        headerName: t('common.tableHeader.id'),
      },
      {
        field: 'name',
        headerName: t('common.tableHeader.name'),
        flex: 1,
        renderCell: (params) => <CellContentTooltip>{params.value}</CellContentTooltip>,
      },
      {
        field: 'type',
        headerName: t('adminProducts.plans.table.typeHeader'),
        width: 120,
      },
      {
        field: 'productCode',
        headerName: t('adminProducts.plans.table.productCode'),
        flex: 1,
      },
      {
        field: 'packages',
        headerName: t('adminProducts.plans.table.packagesHeader'),
        valueGetter: (params) => {
          const count = (params.row as IAdminPlan).packages.reduce((memo, curr) => {
            return memo + curr.amount;
          }, 0);
          return t('adminProducts.plans.table.totalPackages', { count });
        },
        width: 150,
        renderCell: (params) => {
          const items = (params.row as IAdminPlan).packages.map((pack) => ({
            amount: pack.amount,
            name: pack.package?.name,
          }));
          return <CollapsedContentsWithTooltip items={items}>{params.value}</CollapsedContentsWithTooltip>;
        },
      },
      {
        field: 'duration',
        headerName: t('adminProducts.plans.table.durationHeader'),
        valueFormatter: (params) => {
          if (typeof params.value !== 'number') return '-';
          return t('adminProducts.plans.table.duration', { count: params.value });
        },
      },
      {
        field: 'price',
        headerName: t('adminProducts.plans.table.priceHeader'),
        valueGetter: (params) => {
          const value = params.row[params.field];
          if (typeof value !== 'number') return '-';
          return Intl.NumberFormat('en-US', { style: 'currency', currency: params.row.currency }).format(value / 100);
        },
        width: 140,
      },
      {
        field: 'archived',
        headerName: t('adminProducts.plans.table.archivedHeader'),
        renderCell: BoolChip,
        filterOperators: [getBoolFilterOperator()],
      },
      {
        field: 'watermark',
        headerName: t('adminProducts.plans.table.watermarkHeader'),
        renderCell: BoolChip,
        filterOperators: [getBoolFilterOperator()],
      },
      {
        field: 'created',
        headerName: t('common.tableHeader.created'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 120,
      },
      {
        field: 'updated',
        headerName: t('common.tableHeader.updated'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 120,
      },
    ],
    []
  );

  const rows = useMemo(() => {
    if (!data) return null;
    return data.plans;
  }, [data]);

  const getRowLink = useCallback((params: GridRowParams) => queryUrl('edit', params.row.id, search), [search]);

  const isGetRowLinkAllowed = resourceChecker(resourceUpdate);

  if (!data || !rows) return null;
  return (
    <div>
      <FilteringContextProvider quickFilters={quickFilters} defaultSorting={defaultSort}>
        <EasyFilter quickFilters={quickFilters} textFilter={false} />
        <UrlQueryDataGrid
          columns={columns}
          rows={rows}
          disableExtendRowFullWidth={false}
          autoHeight={true}
          disableSelectionOnClick={true}
          getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
        />
        <AddProductPlanDialog open={isAdding} closeDialog={closeDialog} />
        <ProductPlanFetcher.WMF>
          {id ? <EditProductPlanDialog planId={id} closeDialog={abortEdit} /> : null}
        </ProductPlanFetcher.WMF>
      </FilteringContextProvider>
    </div>
  );
};
